import type { QueryClient } from "@tanstack/react-query";

import type { ApiError } from "../../api/client";
import type { Zone } from "../../api/types";
import { attentionSelector, designateSoon } from "../../components/attention";
import { t } from "../../i18n/i18n";
import { type Notice, useUi } from "../../state/ui";
import { ZONE_TARGETS, ZONES_KEY, zoneKey } from "./useZones";
import { zoneRefusalOf } from "./zoneRefusal";
import { type ZoneWait, zoneSuperseded, zoneWait } from "./zoneState";

/** Action attendue d'une zone libre dont le terrain n'est pas (ou plus) utilisable, partout où une action bute sur
 *  elle (R12, docs/14 §2.11) : unités et missions, extension, exports, ordre, analyse du terrain, transmission par
 *  code. La cible est sur la fiche de la zone, dans la vue Manœuvre : carte de progression (attendre), « Réessayer »,
 *  « Libérer de l'espace » ou « Supprimer » (zone sans terre). */

/** Cible de la fiche de zone pour chaque attente. */
export const WAIT_TARGET: Record<ZoneWait, string> = {
  progress: ZONE_TARGETS.progress,
  retry: ZONE_TARGETS.retry,
  storage: ZONE_TARGETS.storage,
  remove: ZONE_TARGETS.remove,
};

/** Vue Manœuvre affichée et cible de la fiche de zone désignée (elle peut ne pas encore exister : la vue entre). */
export function showZoneTarget(target: string): void {
  const ui = useUi.getState();
  if (ui.view !== "manoeuvre") ui.setView("manoeuvre");
  designateSoon(attentionSelector(target));
}

/** Attente d'une zone, refus du serveur compris : une zone prête mais sans terre exploitable (`ZONE_NO_LAND`) attend
 *  sa suppression ; sinon l'attente de son état (`zoneWait`), la préparation par défaut. */
export function zoneWaitOf(zone: Zone | null | undefined, error?: unknown): ZoneWait {
  if (zoneRefusalOf(error) === "ZONE_NO_LAND") return "remove";
  return zoneWait(zone) ?? "progress";
}

/** Libellé du bouton qui mène à l'action attendue (« Voir la préparation », « Réessayer »…). */
export function zoneWaitLabel(zone: Zone | null | undefined, error?: unknown): string {
  if (!zone && zoneRefusalOf(error) !== "ZONE_NO_LAND") return t("Voir le terrain");
  switch (zoneWaitOf(zone, error)) {
    case "retry":
      return zoneSuperseded(zone) ? t("Reprendre la préparation") : t("Réessayer");
    case "storage":
      return t("Libérer de l'espace");
    case "remove":
      return t("Supprimer le terrain");
    default:
      return t("Voir la préparation");
  }
}

/** Mène à l'action attendue de la zone (sa fiche si l'état n'est pas encore connu). */
export function showZoneWait(zone: Zone | null | undefined, error?: unknown): void {
  const known = zone || zoneRefusalOf(error) === "ZONE_NO_LAND";
  showZoneTarget(known ? WAIT_TARGET[zoneWaitOf(zone, error)] : ZONE_TARGETS.card);
}

/** Action d'avis qui mène à l'action attendue d'une zone pas prête. */
export function zoneWaitAction(zone: Zone | null | undefined): NonNullable<Notice["action"]> {
  return { label: zoneWaitLabel(zone), run: () => showZoneWait(zone) };
}

/** Action d'avis « Étendre la zone » (fuseau, graphique ou tracé au bord de l'empreinte). */
export function extendZoneAction(): NonNullable<Notice["action"]> {
  return { label: t("Étendre le terrain"), run: () => showZoneTarget(ZONE_TARGETS.extend) };
}

/** Zone gardée dans un dossier importé ou reçu sans avoir pu être recréée (file pleine, quota, espace) : action
 *  d'avis « Recréer la zone », qui mène au bouton de la vue Manœuvre ; rien si le dossier n'en garde pas. */
export function recreateZoneAction(kept: boolean): Notice["action"] {
  return kept ? { label: t("Recréer le terrain"), run: () => showZoneTarget(ZONE_TARGETS.recreate) } : undefined;
}

/** Le dossier garde-t-il une zone à recréer ? D'après ses paramètres, ou d'après les avis du serveur (réception
 *  d'un code : « Terrain non recréé : … Sa description est gardée dans le dossier… »). */
export function keepsZone(parameters: { extra?: Record<string, unknown> | null } | null | undefined, notices: readonly string[] = []): boolean {
  if (parameters?.extra?.pending_zone) return true;
  return notices.some((n) => n.startsWith("Terrain non recréé : ") && n.includes("Sa description est gardée dans le dossier"));
}

/** Ce que l'action bloquée voulait faire : le texte de l'avis le dit. « corridor » : retouche du fuseau (« Modifier le
 *  fuseau », ajout, retrait et GDH saisi d'une LIMA) ; le fuseau d'un terrain libre se cale sur ses routes, comme celui
 *  d'un camp, une fois le terrain prêt. */
export type ZoneWaitPurpose = "extend" | "terrain" | "export" | "order" | "transmit" | "corridor";

/** Ton de l'avis, accordé à l'encadré (`zoneWaitTone`, R10) : erreur pour une préparation en échec (sans terre
 *  comprise), information sinon (file, préparation, annulation). */
export function zoneNoticeTone(zone: Zone | null | undefined): "error" | "info" {
  return zone?.status === "failed" ? "error" : "info";
}

/** Texte de l'avis d'une action bloquée par une zone pas prête. */
export function zoneWaitText(zone: Zone, purpose: ZoneWaitPurpose): string {
  const wait = zoneWait(zone) ?? "progress";
  const cancelled = zone.status === "cancelled";
  if (wait === "remove") return t("Aucune terre exploitable sur ce terrain : supprimez-le, puis tracez un nouvel axe sur la terre ferme.");
  if (wait === "storage") return t("Espace de stockage insuffisant pour préparer le terrain : libérez de l'espace, puis relancez la préparation.");
  if (wait === "progress") {
    switch (purpose) {
      case "extend":
        return t("Terrain en cours de préparation : attendez la fin avant de l'étendre.");
      case "export":
        return t("Terrain en préparation : les exports seront disponibles quand le terrain sera prêt.");
      case "order":
        return t("Terrain en préparation : l'ordre sera recomposé quand le terrain sera prêt.");
      case "transmit":
        return zone.refreshing
          ? t("Mise à jour des fichiers du terrain en cours : réessayez de transmettre l'ordre par code dans quelques minutes.")
          : t("Extension du terrain en cours : attendez la fin de sa préparation avant de transmettre l'ordre par code.");
      default:
        return t("Disponible quand le terrain sera prêt.");
    }
  }
  if (purpose === "extend")
    return cancelled
      ? t("Préparation du terrain annulée : relancez-la avant de l'étendre.")
      : t("Préparation du terrain en échec : relancez-la avant de l'étendre.");
  if (purpose === "terrain")
    return cancelled
      ? t("Préparation du terrain annulée : relancez-la pour placer unités et missions.")
      : t("Préparation du terrain en échec : relancez-la pour placer unités et missions.");
  return cancelled
    ? t("Préparation du terrain annulée : relancez-la dans la vue Manœuvre.")
    : t("Préparation du terrain en échec : relancez-la dans la vue Manœuvre.");
}

/** Action bloquée tant que la zone n'est pas utilisable : l'avis dit pourquoi et l'action attendue est désignée (la
 *  fiche de la zone tant que son état n'est pas encore relu). */
export function designateZoneWait(zone: Zone | null, purpose: ZoneWaitPurpose): void {
  useUi.getState().notify({ text: zone ? zoneWaitText(zone, purpose) : t("Disponible quand le terrain sera prêt."), tone: zoneNoticeTone(zone) });
  showZoneWait(zone);
}

/** Retouche du fuseau (`PUT /corridor`) demandée sur un terrain libre pas encore prêt : le serveur la refuse (409) ;
 *  l'avis le dit et désigne l'action attendue (carte de progression, « Réessayer »…). Rend vrai si l'action est
 *  bloquée. Les glissements de LIMA restent possibles pendant la préparation. */
export function refuseCorridorWhilePreparing(zone: Zone | null | undefined): boolean {
  if (!zone || zone.status === "ready") return false;
  designateZoneWait(zone, "corridor");
  return true;
}

/** Zone libre du dossier ouvert lue dans le cache : son dernier état relevé, sinon celui de la liste ; null pour un
 *  camp ou une zone inconnue du cache. */
export function openZoneFromCache(queryClient: QueryClient): Zone | null {
  const slug = useUi.getState().mapSlug;
  if (!slug) return null;
  const listed = queryClient.getQueryData<Zone[]>(ZONES_KEY)?.find((z) => z.slug === slug);
  if (!listed) return null;
  return queryClient.getQueryData<Zone>(zoneKey(listed.id)) ?? listed;
}

/** Refus du serveur propre à la zone libre (`zoneRefusalOf`) : avis avec son message et l'action attendue — la fiche
 *  de la zone est désignée si la vue Manœuvre est affichée, sinon l'avis y mène. `title` : ce qui n'a pas été fait
 *  (« Déplacement non enregistré ») ; le message du serveur passe alors en détail. Le dispositif et la zone sont
 *  relus (unités retirées tant que le terrain n'est pas prêt). `retry` : rejoue l'écriture (zone tenue un instant).
 *  Vrai si le refus a été traité ici. */
export function handleZoneRefusal(queryClient: QueryClient, error: unknown, title?: string, retry?: () => void): boolean {
  const code = zoneRefusalOf(error);
  if (!code) return false;
  const message = (error as ApiError).message;
  const ui = useUi.getState();
  const onManoeuvre = ui.view === "manoeuvre";
  const zone = openZoneFromCache(queryClient);
  const text = title ?? message;
  const detail = title ? message : undefined;
  switch (code) {
    case "ZONE_BUSY":
      // Ligne de la zone tenue un instant par l'écriture finale du service : « Réessayer » quand l'appelant sait
      // rejouer l'écriture (`retry`) ; sinon le message (« réessayez dans un instant ») suffit.
      ui.notify({ text, detail, tone: "info", action: retry ? { label: t("Réessayer"), run: retry } : undefined });
      break;
    case "CORRIDOR_OUTSIDE":
      ui.notify({ text, detail, tone: "warn", action: extendZoneAction() });
      if (onManoeuvre) showZoneTarget(ZONE_TARGETS.extend);
      break;
    case "ZONE_NO_LAND":
      ui.notify({ text, detail, tone: "error", action: { label: t("Supprimer le terrain"), run: () => showZoneTarget(ZONE_TARGETS.remove) } });
      if (onManoeuvre) showZoneTarget(ZONE_TARGETS.remove);
      break;
    default:
      // Terrain pas prêt, ou extension en cours (code de transmission) : l'action attendue de la zone.
      ui.notify({ text, detail, tone: zoneNoticeTone(zone), action: zoneWaitAction(zone) });
      if (onManoeuvre) showZoneWait(zone);
  }
  if (code === "ZONE_NOT_READY" || code === "ZONE_NO_LAND") {
    void queryClient.invalidateQueries({ queryKey: ["deployment"] });
    void queryClient.invalidateQueries({ queryKey: ZONES_KEY });
    if (zone) void queryClient.invalidateQueries({ queryKey: zoneKey(zone.id) });
  }
  return true;
}
